import {
  Ruler,
  Anchor,
  Zap,
  Footprints,
  Dumbbell,
  Activity,
  ShieldCheck,
  Shuffle,
  Gauge,
  Shield,
  Scale,
  TrendingUp,
  Layers,
  ClipboardCheck,
  Rocket,
} from "lucide-react";
import Reveal from "./Reveal";

const WEEKS = [
  {
    n: "01",
    tag: "Week One",
    sessions: "4 Sessions",
    title: "Establishing the Foundation",
    items: [
      { icon: Ruler, term: "Core Basics", body: "Understand body alignment and how to safely stack your joints." },
      { icon: Anchor, term: "Upper-Body Stability", body: "Master scapula control for a strong, safe base." },
      { icon: Zap, term: "Mind-Muscle Connection", body: "Learn to actively feel and engage the correct muscles." },
      { icon: Footprints, term: "Practical Application", body: "Put your new foundational mechanics into motion." },
    ],
  },
  {
    n: "02",
    tag: "Week Two",
    sessions: "4 Sessions",
    title: "Building Gym Confidence",
    items: [
      { icon: Dumbbell, term: "Machine Introduction", body: "Overcome the fear of the unknown with gym equipment." },
      { icon: Activity, term: "Productive Movement", body: "Identify your body's most optimal and safe ranges of motion." },
      { icon: ShieldCheck, term: "Navigating Restrictions", body: "Train safely around your personal physical limits." },
      { icon: Shuffle, term: "Confident Execution", body: "Set up and transition smoothly between exercises." },
    ],
  },
  {
    n: "03",
    tag: "Week Three",
    sessions: "4 Sessions",
    title: "Control & Mechanics",
    items: [
      { icon: Gauge, term: "Tension Control", body: "Use pacing and tempo to maximize each workout." },
      { icon: Shield, term: "Core Bracing", body: "Protect your spine and stay stable under load." },
      { icon: Scale, term: "Body Balance", body: "Understand foundational push and pull dynamics." },
      { icon: TrendingUp, term: "Progressive Overload", body: "The core principle for safely building strength over time." },
    ],
  },
  {
    n: "04",
    tag: "Week Four",
    sessions: "3 Sessions",
    title: "Generating Momentum",
    items: [
      { icon: Layers, term: "Full-Body Integration", body: "Combine every mechanic into fluid, confident movement." },
      { icon: ClipboardCheck, term: "Confidence Assessment", body: "Reinforce your foundation to lock in your progress." },
      { icon: Rocket, term: "Sustaining Momentum", body: "Carry your new skills into your ultimate fitness goals." },
    ],
  },
];

export default function Curriculum() {
  return (
    <section
      id="curriculum"
      data-testid="curriculum-section"
      className="relative py-28 sm:py-40 px-5 sm:px-8 bg-[#0a0a0a] border-y border-white/5"
    >
      <div className="max-w-[1400px] mx-auto">
        <Reveal>
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#d4af37] mb-5">
            Your Roadmap
          </p>
          <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
            The 4-week <span className="gold-text">curriculum</span>
          </h2>
          <p className="mt-6 text-lg font-light text-neutral-300 leading-relaxed max-w-2xl">
            Fifteen coached sessions across four focused weeks — taking you from
            the fundamentals to real, lasting momentum. Here&apos;s exactly what
            you&apos;ll learn and walk away with.
          </p>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-2 gap-5">
          {WEEKS.map((week, wi) => (
            <Reveal key={week.n} delay={(wi % 2) * 0.1}>
              <div
                data-testid={`curriculum-week-${wi}`}
                className="group h-full bg-[#111111] border border-white/10 p-8 transition-all duration-500 hover:border-[#d4af37]/50 hover:-translate-y-1"
              >
                <div className="flex items-center gap-4 mb-8">
                  <span className="font-display font-black text-5xl sm:text-6xl text-[#d4af37]/25 leading-none transition-colors duration-500 group-hover:text-[#d4af37]/50">
                    {week.n}
                  </span>
                  <div>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] font-bold">
                      {week.tag} · {week.sessions}
                    </p>
                    <h3 className="font-display font-bold text-xl leading-tight mt-1">
                      {week.title}
                    </h3>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-6">
                  {week.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.term} className="flex gap-4">
                        <div className="shrink-0 w-10 h-10 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-[#d4af37]" strokeWidth={1.6} />
                        </div>
                        <div>
                          <p className="font-semibold text-white text-sm">{item.term}</p>
                          <p className="text-sm text-neutral-400 leading-relaxed mt-0.5">
                            {item.body}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
